/**
 * Pure client-side attachment helpers (F-006, NF-005). The server remains the
 * authority (MIME sniffing, byte caps, count limit); these mirror the budgets
 * for an early, friendly rejection before any bytes leave the browser.
 */

/** F-006: max 25 MB per file (mirrors api MAX_ATTACHMENT_SIZE_BYTES). */
export const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

/** F-006: max 10 files per upload request (mirrors api MAX_FILES_PER_UPLOAD). */
export const MAX_FILES_PER_UPLOAD = 10;

/**
 * The NF-005 positive list as file extensions - exactly the SRS list, matching
 * the api's EXTENSION_RULES (validation.ts). Drives the file input's `accept`.
 */
export const ACCEPTED_FILE_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".pdf",
  ".txt",
  ".log",
  ".csv",
  ".json",
  ".yaml",
  ".md",
  ".zip",
  ".tar",
  ".tar.gz",
  ".docx",
  ".xlsx",
] as const;

/** `accept` attribute value for the upload input. */
export const ACCEPT_ATTRIBUTE = ACCEPTED_FILE_EXTENSIONS.join(",");

/** The subset of File the validator needs (keeps the helper DOM-free). */
export interface FileLike {
  readonly name: string;
  readonly size: number;
}

export interface FileValidation {
  /** True when the selection may be uploaded as-is. */
  ok: boolean;
  /** Names of files exceeding the 25 MB per-file budget. */
  tooLarge: string[];
  /** True when more than 10 files were selected. */
  tooMany: boolean;
}

/**
 * Pre-upload check of a file selection against the F-006 budgets (25 MB per
 * file, 10 files per request). Type checking stays server-side (NF-005 needs
 * content sniffing); an empty selection is trivially ok.
 */
export function validateFiles(files: readonly FileLike[]): FileValidation {
  const tooLarge = files
    .filter((file) => file.size > MAX_FILE_SIZE_BYTES)
    .map((file) => file.name);
  const tooMany = files.length > MAX_FILES_PER_UPLOAD;
  return { ok: tooLarge.length === 0 && !tooMany, tooLarge, tooMany };
}

/**
 * Human-readable size, German locale (decimal comma). Bytes below 1 KB stay
 * whole; KB/MB/GB carry at most one fraction digit. Negative or non-finite
 * input renders as the shared em-dash placeholder.
 */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "-";
  if (n < 1024) return `${n.toLocaleString("de-DE")} B`;
  const units = ["KB", "MB", "GB"] as const;
  let value = n / 1024;
  let unit: (typeof units)[number] = "KB";
  for (const next of units) {
    unit = next;
    if (value < 1024 || next === "GB") break;
    value /= 1024;
  }
  const rendered = value.toLocaleString("de-DE", { maximumFractionDigits: 1 });
  return `${rendered} ${unit}`;
}

/**
 * RFC-7807 problem `title` carries the backend i18n key (api problem builder);
 * this maps the attachment-specific ones to SPA catalog keys so the toast is
 * localized even when the server `detail` is absent. Unknown titles fall back
 * to the generic notifyApiError handling (same pattern as the postmortem map).
 */
export const ATTACHMENT_PROBLEM_KEY: Record<string, string> = {
  "error.attachment_type_not_allowed": "errors.attachmentTypeNotAllowed",
  "error.attachment_too_large": "errors.attachmentTooLarge",
  "error.attachment_count_exceeded": "errors.attachmentCountExceeded",
  "error.attachment_already_removed": "errors.attachmentAlreadyRemoved",
  "error.attachment_gone": "errors.attachmentGone",
  "error.attachment_not_downloadable": "errors.attachmentNotDownloadable",
};

/**
 * Extract the failing filename from a problem's structured field errors. The
 * api formats upload rejections as "{filename}: {reason}" on the `files` path;
 * anything else yields undefined and the toast stays file-less.
 */
export function problemFilename(
  errors: ReadonlyArray<{ path: string; message: string }> | undefined,
): string | undefined {
  const message = errors?.find((e) => e.path === "files")?.message;
  if (!message) return undefined;
  const sep = message.indexOf(": ");
  return sep > 0 ? message.slice(0, sep) : undefined;
}

/**
 * The image types the api accepts (NF-005: png, jpeg, gif, webp) - and only
 * those, because the server sniffed them. SVG is deliberately absent: it is
 * not on the positive list, and it is the one image type that carries script.
 */
const PREVIEWABLE_IMAGE_MIMES: ReadonlySet<string> = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
]);

/**
 * [F-006]/AC-070: a file attachment whose content is an image is shown as a
 * preview. A link, a removed slot (the blob is gone, F-017/GR-006) or any other
 * type is not.
 */
export function isPreviewableImage(attachment: {
  readonly kind: string;
  readonly mimeType: string | null;
  readonly removedAt: string | null;
}): boolean {
  return (
    attachment.kind === "datei" &&
    attachment.removedAt === null &&
    attachment.mimeType !== null &&
    PREVIEWABLE_IMAGE_MIMES.has(attachment.mimeType.toLowerCase())
  );
}
